<template>
  <div class="entity-record-view">
    <nav class="breadcrumbs">
      <router-link :to="{ name: 'Projects' }">Projects</router-link>
      <span class="breadcrumb-sep">/</span>
      <router-link :to="{ name: 'ComplianceDashboard', params: { id: projectId } }">
        {{ header.project_name || 'Project' }}
      </router-link>
      <span class="breadcrumb-sep">/</span>
      <span class="breadcrumb-current">{{ header.entity_name || 'Entity Record' }}</span>
    </nav>

    <div class="record-header">
      <div class="header-left">
        <button class="btn btn-ghost back-btn" @click="goBack">
          <i class="fas fa-arrow-left"></i> Back to project
        </button>
        <div class="title-block">
          <h1>{{ header.entity_name || 'Entity Record' }}</h1>
          <p class="subtitle">Compliance record for {{ header.project_name || 'this project' }}</p>
        </div>
      </div>
      <div class="header-right">
        <div v-if="healthAvailable" class="score-circle" :class="scoreClass">
          {{ header.health.overall_score }}%
        </div>
        <span v-if="header.current_status" class="status-badge" :class="statusClass">
          {{ header.current_status_display || header.current_status }}
        </span>
        <span v-if="entityRequirementsLabel" class="requirements-pill">
          {{ entityRequirementsLabel }}
        </span>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i> Loading entity record…
    </div>

    <div v-else-if="error" class="error-state">
      <i class="fas fa-exclamation-triangle"></i>
      <p>{{ error }}</p>
      <button class="btn btn-secondary" @click="loadAll">Retry</button>
    </div>

    <template v-else>
      <!-- Gap-led verdict strip -->
      <section v-if="issueSummary" class="verdict-card" :class="verdictCardClass">
        <div class="verdict-main">
          <p class="verdict-headline">{{ issueSummary.headline }}</p>
          <p v-if="issueSummary.action" class="verdict-action">
            <i class="fas fa-arrow-right"></i>
            {{ issueSummary.action }}
            <span v-if="issueSummary.urgency" class="urgency-tag">{{ issueSummary.urgency }}</span>
          </p>
          <p v-else-if="healthAvailable && header.health.executive_summary" class="verdict-summary">
            {{ header.health.executive_summary }}
          </p>
        </div>
        <div v-if="issueSummary.next_expiry" class="verdict-expiry">
          <span class="expiry-label">{{ issueSummary.next_expiry.coverage }} expires</span>
          <span class="expiry-date">{{ formatDate(issueSummary.next_expiry.date) }}</span>
          <span class="expiry-days">({{ formatExpiryDays(issueSummary.next_expiry.days) }})</span>
        </div>
      </section>

      <div class="tab-bar">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          class="tab-btn"
          :class="{ active: activeTab === tab.id }"
          @click="activeTab = tab.id"
        >
          <i :class="tab.icon"></i> {{ tab.label }}
        </button>
      </div>

      <!-- Overview -->
      <div v-if="activeTab === 'overview'" class="tab-panel">
        <!-- Coverage strip -->
        <section v-if="coverageEntries.length" class="section-block">
          <h2><i class="fas fa-shield-alt"></i> Coverage posture</h2>
          <p class="section-hint">Entity-level view across all valid documents for this project check.</p>
          <div class="coverage-strip">
            <div
              v-for="[type, data] in coverageEntries"
              :key="type"
              class="coverage-chip"
              :class="coverageChipClass(data.status)"
            >
              <span class="chip-type">{{ type }}</span>
              <span class="chip-status">{{ formatCoverageStatus(data.status) }}</span>
              <span v-if="data.score != null" class="chip-score">{{ data.score }}%</span>
            </div>
          </div>
        </section>

        <!-- Root causes -->
        <section v-if="rootCauses.length" class="section-block">
          <h2><i class="fas fa-exclamation-circle"></i> Open gaps</h2>
          <div class="root-cause-list">
            <div v-for="(cause, idx) in rootCauses" :key="idx" class="root-cause-card">
              <div class="cause-title">{{ cause.title }}</div>
              <p v-if="cause.description" class="cause-desc">{{ cause.description }}</p>
              <p v-if="cause.resolution" class="cause-resolution">
                <i class="fas fa-wrench"></i> {{ cause.resolution }}
              </p>
            </div>
          </div>
        </section>

        <!-- Upcoming expirations from health snapshot -->
        <section v-if="upcomingExpirations.length" class="section-block">
          <h2><i class="fas fa-calendar-alt"></i> Upcoming expirations</h2>
          <ul class="expiry-list">
            <li v-for="(exp, idx) in upcomingExpirations" :key="idx">
              <strong>{{ exp.coverage_type || exp.coverage }}</strong>
              — {{ formatDate(exp.expires || exp.date) }}
              <span class="expiry-days-inline">({{ formatExpiryDays(exp.days_until ?? exp.days) }})</span>
            </li>
          </ul>
        </section>

        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-value">{{ header.stats?.completed_check_runs || 0 }}</div>
            <div class="stat-label">Completed analyses</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ header.stats?.analysis_sessions || 0 }}</div>
            <div class="stat-label">Analysis sessions</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ header.stats?.coverage_intervals || 0 }}</div>
            <div class="stat-label">Coverage intervals tracked</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">{{ header.stats?.status_change_events || 0 }}</div>
            <div class="stat-label">Status changes logged</div>
          </div>
        </div>

        <section v-if="trend.points.length" class="section-block">
          <h2><i class="fas fa-file-alt"></i> Per-document check samples</h2>
          <p class="section-hint">
            Each row is one certificate analysis — not the entity-level verdict.
            Use the coverage posture and open gaps above for compliance status.
          </p>
          <div class="trend-table-wrap">
            <table class="trend-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Document</th>
                  <th>Met</th>
                  <th>%</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="point in trendReversed" :key="point.check_run_id">
                  <td>{{ formatDate(point.date) }}</td>
                  <td>{{ point.document_name || '—' }}</td>
                  <td>{{ point.passed }}/{{ point.total }}</td>
                  <td>{{ point.percent_met }}%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
        <div v-else class="empty-hint">
          No completed check runs yet — upload and analyze a certificate to start building history.
        </div>
      </div>

      <!-- Activity -->
      <div v-if="activeTab === 'timeline'" class="tab-panel">
        <section class="section-block">
          <h2><i class="fas fa-stream"></i> Activity</h2>
          <div v-if="timeline.events.length" class="timeline-list">
            <div
              v-for="(event, idx) in timeline.events"
              :key="`${event.type}-${event.timestamp}-${idx}`"
              class="timeline-item"
            >
              <div
                class="timeline-marker"
                :class="[`type-${event.type}`, { 'is-problem': event.problem }]"
              ></div>
              <div class="timeline-content">
                <div class="timeline-title">{{ event.title }}</div>
                <div class="timeline-meta">{{ formatDateTime(event.timestamp) }}</div>
                <button
                  v-if="event.type === 'check_run' && event.detail?.check_run_id"
                  class="btn-link"
                  @click="openCheckRun(event.detail)"
                >
                  View results →
                </button>
              </div>
            </div>
          </div>
          <div v-else class="empty-hint">No activity yet.</div>
          <button
            v-if="timeline.has_more"
            class="btn btn-outline load-more-btn"
            :disabled="loadingMoreTimeline"
            @click="loadMoreTimeline"
          >
            <i :class="loadingMoreTimeline ? 'fas fa-spinner fa-spin' : 'fas fa-chevron-down'"></i>
            Load more
          </button>
        </section>
      </div>

      <!-- Documents -->
      <div v-if="activeTab === 'documents'" class="tab-panel documents-tab">
        <EntityDocumentsModal
          :show="true"
          :embedded="true"
          :subject-id="subjectId"
          :entity-name="header.entity_name"
          @close="() => {}"
        />
      </div>

      <!-- Coverage -->
      <div v-if="activeTab === 'continuity'" class="tab-panel">
        <EntityPolicyInventory
          :documents="policyDocuments"
          :subject-id="subjectId"
          @waived="onWaiverSaved"
        />
        <section class="section-block">
          <div class="section-heading-row">
            <div>
              <h2><i class="fas fa-history"></i> Coverage over time</h2>
              <p class="section-hint">
                Period-of-time view: covered intervals and gaps between effective and expiration dates.
              </p>
            </div>
            <button
              v-if="continuity.has_interval_data"
              class="btn btn-outline btn-sm"
              :disabled="exportingContinuity"
              @click="exportContinuity"
            >
              <i :class="exportingContinuity ? 'fas fa-spinner fa-spin' : 'fas fa-download'"></i>
              Export attestation
            </button>
          </div>

          <div v-if="!continuity.has_interval_data" class="empty-hint">
            <span v-if="policyDocuments.length">
              No period-of-time intervals yet. The policies above are from the latest certificate checks.
            </span>
            <span v-else>
              No coverage intervals recorded yet. Upload and analyze certificates to build coverage history.
            </span>
          </div>

          <div v-else class="continuity-list">
            <div
              v-for="cov in continuity.coverage_types"
              :key="cov.coverage_type"
              class="continuity-card"
            >
              <div class="continuity-card-header">
                <strong>{{ cov.coverage_type }}</strong>
                <span class="continuity-pct">{{ cov.continuity_percent }}% covered</span>
              </div>
              <div class="continuity-bar-track">
                <div
                  class="continuity-bar-fill"
                  :style="{ width: `${cov.continuity_percent}%` }"
                ></div>
              </div>
              <div class="continuity-meta">
                {{ cov.covered_days }} / {{ cov.total_days }} days
                <span v-if="cov.lapse_count"> · {{ cov.lapse_count }} lapse(s)</span>
                <span v-if="cov.interval_count"> · {{ cov.interval_count }} interval(s)</span>
              </div>
              <ul v-if="cov.lapses.length" class="lapse-list">
                <li v-for="(lapse, i) in cov.lapses" :key="i">
                  Lapse {{ formatDate(lapse.start) }} – {{ formatDate(lapse.end) }} ({{ lapse.days }} days)
                </li>
              </ul>
              <div v-if="cov.intervals && cov.intervals.length" class="interval-list">
                <div
                  v-for="interval in cov.intervals"
                  :key="interval.id"
                  class="interval-row"
                  :class="{ 'low-confidence': interval.low_confidence }"
                >
                  <span>{{ formatDate(interval.effective_date) }} – {{ formatDate(interval.expiration_date) }}</span>
                  <span class="interval-badges">
                    <span class="confidence-badge">{{ Math.round(interval.confidence * 100) }}%</span>
                    <span class="source-badge">{{ formatIntervalSource(interval.source) }}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- Parties -->
      <div v-if="activeTab === 'parties'" class="tab-panel">
        <EntityPartiesEditor
          :subject-id="subjectId"
          :agent-links="agentLinks"
          @updated="loadHeader"
        />

        <section v-if="policyExpiryEntries.length" class="section-block">
          <h2><i class="fas fa-calendar-check"></i> Policy expiration dates</h2>
          <ul class="expiry-list">
            <li v-for="entry in policyExpiryEntries" :key="entry.coverage">
              <strong>{{ entry.coverage }}</strong> — {{ formatDate(entry.date) }}
            </li>
          </ul>
        </section>

        <section v-if="header.entity_id" class="section-block">
          <h2><i class="fas fa-map-marker-alt"></i> Locations</h2>
          <p class="section-purpose">
            FranConnect store numbers for this franchisee. A long number is the
            FranConnect reference id, not the store number printed on a certificate.
          </p>
          <div v-if="loadingLocations" class="empty-hint"><i class="fas fa-spinner fa-spin"></i> Loading…</div>
          <div v-else-if="locations.length" class="locations-list">
            <div v-for="loc in locations" :key="loc.id" class="location-row">
              <strong>{{ loc.identity_label || loc.name || loc.external_location_id || 'Location' }}</strong>
              <span v-if="loc.is_active === false" class="location-status">Inactive</span>
              <span v-if="loc.identity_meta" class="location-meta">{{ loc.identity_meta }}</span>
              <span v-if="locationAddress(loc)" class="location-address">{{ locationAddress(loc) }}</span>
            </div>
          </div>
          <div v-else class="empty-hint">No locations on file for this entity.</div>
        </section>
      </div>
    </template>

    <AnalysisResultsModal
      v-if="showAnalysisResults && analysisData"
      :show="showAnalysisResults"
      :check-run-id="analysisData.checkRunId"
      :document-id="analysisData.documentId"
      @close="showAnalysisResults = false"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/services/api';
import EntityDocumentsModal from '@/components/compliance/EntityDocumentsModal.vue';
import AnalysisResultsModal from '@/components/compliance/AnalysisResultsModal.vue';
import EntityPartiesEditor from '@/components/compliance/EntityPartiesEditor.vue';
import EntityPolicyInventory from '@/components/compliance/EntityPolicyInventory.vue';

export default {
  name: 'EntityRecordView',
  components: { EntityDocumentsModal, AnalysisResultsModal, EntityPartiesEditor, EntityPolicyInventory },
  props: {
    projectId: { type: [String, Number], required: true },
    subjectId: { type: [String, Number], required: true },
  },
  setup(props) {
    const route = useRoute();
    const router = useRouter();

    const loading = ref(true);
    const error = ref(null);
    const tabs = [
      { id: 'overview', label: 'Overview', icon: 'fas fa-chart-pie' },
      { id: 'timeline', label: 'Activity', icon: 'fas fa-stream' },
      { id: 'documents', label: 'Documents', icon: 'fas fa-folder-open' },
      { id: 'continuity', label: 'Coverage', icon: 'fas fa-shield-alt' },
      { id: 'parties', label: 'Parties', icon: 'fas fa-users' },
    ];

    const resolveInitialTab = () => {
      const queryTab = route.query.tab;
      if (queryTab && tabs.some((t) => t.id === queryTab)) {
        return queryTab;
      }
      return 'overview';
    };

    const activeTab = ref(resolveInitialTab());
    const header = ref({});
    const trend = ref({ points: [] });
    const timeline = ref({ events: [], has_more: false, offset: 0, limit: 50 });
    const continuity = ref({ coverage_types: [], has_interval_data: false });
    const loadingMoreTimeline = ref(false);
    const showAnalysisResults = ref(false);
    const analysisData = ref(null);
    const exportingContinuity = ref(false);
    const locations = ref([]);
    const loadingLocations = ref(false);
    const locationsLoaded = ref(false);

    const trendReversed = computed(() => [...(trend.value.points || [])].reverse());

    const issueSummary = computed(() => header.value.issue_summary || null);
    const healthAvailable = computed(() => header.value.health?.available === true);
    const rootCauses = computed(() => header.value.health?.root_causes || []);
    const upcomingExpirations = computed(() => header.value.health?.upcoming_expirations || []);
    const agentLinks = computed(() => header.value.parties?.agent_links || []);
    const policyDocuments = computed(() => continuity.value.policy_documents || []);
    const coverageEntries = computed(() => {
      const scores = header.value.health?.coverage_scores || {};
      return Object.entries(scores);
    });

    const policyExpiryEntries = computed(() => {
      const raw = header.value.parties?.policy_expiration_dates || {};
      return Object.entries(raw)
        .filter(([, date]) => date)
        .map(([coverage, date]) => ({ coverage, date }))
        .sort((a, b) => String(a.date).localeCompare(String(b.date)));
    });

    const entityRequirementsLabel = computed(() => {
      const h = header.value.health;
      if (!h?.available || !h.total_requirements) return null;
      return `${h.passing_requirements}/${h.total_requirements} requirements met`;
    });

    const statusClass = computed(() => {
      const status = (header.value.current_status || '').toLowerCase();
      if (status === 'compliant') return 'status-compliant';
      if (status.includes('non') || status.includes('action')) return 'status-warning';
      return 'status-pending';
    });

    const scoreClass = computed(() => {
      const score = header.value.health?.overall_score ?? 0;
      if (score >= 80) return 'score-good';
      if (score >= 60) return 'score-warn';
      return 'score-bad';
    });

    const verdictCardClass = computed(() => {
      const status = (header.value.current_status || '').toUpperCase();
      if (status === 'COMPLIANT') return 'verdict-compliant';
      if (status.includes('NON') || status === 'ACTION_NEEDED') return 'verdict-action';
      return 'verdict-pending';
    });

    const formatDate = (value) => {
      if (!value) return 'N/A';
      return new Date(value).toLocaleDateString();
    };

    const formatDateTime = (value) => {
      if (!value) return 'N/A';
      return new Date(value).toLocaleString();
    };

    const formatExpiryDays = (days) => {
      if (days == null) return '';
      if (days < 0) return `${Math.abs(days)} days ago`;
      if (days === 0) return 'today';
      if (days === 1) return '1 day';
      return `${days} days`;
    };

    const formatCoverageStatus = (status) => (status || 'UNKNOWN').replace(/_/g, ' ');

    const coverageChipClass = (status) => {
      const s = (status || '').toUpperCase();
      if (s === 'COMPLIANT') return 'chip-compliant';
      if (s === 'PARTIAL') return 'chip-partial';
      if (s === 'MISSING') return 'chip-missing';
      return 'chip-unknown';
    };

    const formatIntervalSource = (source) => {
      const labels = {
        ANALYSIS_EXTRACTION: 'Live',
        BACKFILL: 'Backfill',
        MANUAL_OVERRIDE: 'Manual',
      };
      return labels[source] || source || 'Unknown';
    };

    const loadHeader = async () => {
      const res = await api.get(`/compliance-subjects/${props.subjectId}/entity-record/`);
      header.value = res.data;
    };

    const loadTrend = async () => {
      const res = await api.get(`/compliance-subjects/${props.subjectId}/compliance-trend/`);
      trend.value = res.data;
    };

    const loadTimeline = async (append = false) => {
      const offset = append ? timeline.value.offset + timeline.value.limit : 0;
      const res = await api.get(`/compliance-subjects/${props.subjectId}/timeline/`, {
        params: { limit: timeline.value.limit, offset },
      });
      const data = res.data;
      timeline.value = {
        events: append ? [...timeline.value.events, ...(data.events || [])] : (data.events || []),
        has_more: data.has_more,
        offset,
        limit: timeline.value.limit,
      };
    };

    const loadContinuity = async () => {
      const res = await api.get(`/compliance-subjects/${props.subjectId}/continuity/`);
      continuity.value = res.data;
    };

    const onWaiverSaved = async () => {
      try {
        await api.post('/compliance-health/calculate/', {
          subject_id: props.subjectId,
          skip_llm: true,
        });
      } catch {
        // The waiver is already saved. Score refresh is limited to admins.
      }
      await Promise.all([loadHeader(), loadContinuity()]);
    };

    const locationAddress = (loc) => {
      if (loc.address) return loc.address;
      return [loc.address_line1, loc.city, loc.state, loc.postal_code].filter(Boolean).join(', ');
    };

    const loadLocations = async () => {
      if (!header.value.entity_id || locationsLoaded.value) return;
      loadingLocations.value = true;
      try {
        const res = await api.get(`/entities/${header.value.entity_id}/locations/`);
        locations.value = res.data?.locations || res.data?.results || res.data || [];
        locationsLoaded.value = true;
      } catch {
        locations.value = [];
      } finally {
        loadingLocations.value = false;
      }
    };

    const loadAll = async () => {
      loading.value = true;
      error.value = null;
      try {
        await Promise.all([loadHeader(), loadTrend(), loadTimeline(), loadContinuity()]);
      } catch (e) {
        error.value = e?.response?.data?.error || e?.message || 'Failed to load entity record';
      } finally {
        loading.value = false;
      }
    };

    const loadMoreTimeline = async () => {
      loadingMoreTimeline.value = true;
      try {
        await loadTimeline(true);
      } finally {
        loadingMoreTimeline.value = false;
      }
    };

    const goBack = () => {
      router.push({ name: 'ComplianceDashboard', params: { id: props.projectId } });
    };

    const openCheckRun = (detail) => {
      analysisData.value = {
        checkRunId: detail.check_run_id,
        documentId: detail.document_id,
      };
      showAnalysisResults.value = true;
    };

    const exportContinuity = async () => {
      exportingContinuity.value = true;
      try {
        const res = await api.get(`/compliance-subjects/${props.subjectId}/continuity/export/`, {
          responseType: 'blob',
        });
        const blob = new Blob([res.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `continuity_attestation_${props.subjectId}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        error.value = e?.response?.data?.error || e?.message || 'Failed to export continuity attestation';
      } finally {
        exportingContinuity.value = false;
      }
    };

    watch(
      () => route.query.tab,
      (tab) => {
        if (tab && tabs.some((t) => t.id === tab)) {
          activeTab.value = tab;
        }
      }
    );

    watch(activeTab, (tab) => {
      if (tab === 'parties') {
        loadLocations();
      }
    });

    const openAnalysisFromQuery = () => {
      const checkRunId = route.query.checkRunId;
      const documentId = Number(route.query.documentId);
      if (checkRunId && Number.isFinite(documentId) && documentId > 0) {
        analysisData.value = {
          checkRunId: String(checkRunId),
          documentId,
        };
        showAnalysisResults.value = true;
      }
    };

    onMounted(() => {
      activeTab.value = resolveInitialTab();
      openAnalysisFromQuery();
      loadAll();
    });

    watch(
      () => [route.query.checkRunId, route.query.documentId],
      () => openAnalysisFromQuery(),
    );

    return {
      loading,
      error,
      activeTab,
      tabs,
      header,
      trend,
      trendReversed,
      timeline,
      continuity,
      loadingMoreTimeline,
      showAnalysisResults,
      analysisData,
      exportingContinuity,
      locations,
      loadingLocations,
      locationAddress,
      issueSummary,
      healthAvailable,
      rootCauses,
      upcomingExpirations,
      agentLinks,
      policyDocuments,
      coverageEntries,
      policyExpiryEntries,
      entityRequirementsLabel,
      statusClass,
      scoreClass,
      verdictCardClass,
      formatDate,
      formatDateTime,
      formatExpiryDays,
      formatCoverageStatus,
      coverageChipClass,
      formatIntervalSource,
      Math,
      loadAll,
      loadHeader,
      onWaiverSaved,
      loadMoreTimeline,
      goBack,
      openCheckRun,
      exportContinuity,
    };
  },
};
</script>

<style scoped>
.entity-record-view {
  padding: 1.5rem 2rem 3rem;
  max-width: 1200px;
  margin: 0 auto;
}

.breadcrumbs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.85rem;
  color: #64748b;
  margin-bottom: 1rem;
}

.breadcrumbs a {
  color: #4338ca;
  text-decoration: none;
}

.breadcrumbs a:hover {
  text-decoration: underline;
}

.breadcrumb-sep {
  color: #cbd5e1;
}

.breadcrumb-current {
  color: #334155;
  font-weight: 500;
}

.verdict-card {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  margin-bottom: 1.5rem;
  background: #f8fafc;
}

.verdict-compliant {
  border-left: 4px solid #22c55e;
}

.verdict-action {
  border-left: 4px solid #f59e0b;
  background: #fffbeb;
}

.verdict-pending {
  border-left: 4px solid #94a3b8;
}

.verdict-headline {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.4;
}

.verdict-action {
  margin: 0.5rem 0 0;
  font-size: 0.9rem;
  color: #4338ca;
}

.verdict-action i {
  margin-right: 0.35rem;
}

.verdict-summary {
  margin: 0.5rem 0 0;
  font-size: 0.875rem;
  color: #475569;
  line-height: 1.45;
}

.urgency-tag {
  margin-left: 0.5rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  background: #fee2e2;
  color: #b91c1c;
}

.verdict-expiry {
  flex-shrink: 0;
  text-align: right;
  font-size: 0.85rem;
}

.expiry-label {
  display: block;
  color: #64748b;
}

.expiry-date {
  font-weight: 600;
  color: #0f172a;
}

.expiry-days {
  color: #64748b;
  margin-left: 0.25rem;
}

.section-heading-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
}

.record-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.back-btn {
  align-self: flex-start;
}

.title-block h1 {
  margin: 0;
  font-size: 1.5rem;
}

.subtitle {
  margin: 0.25rem 0 0;
  color: var(--text-secondary, #666);
  font-size: 0.9rem;
}

.header-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
}

.score-circle {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
}

.score-good { background: #dcfce7; color: #166534; }
.score-warn { background: #fef9c3; color: #854d0e; }
.score-bad { background: #fee2e2; color: #991b1b; }

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-compliant { background: #d4edda; color: #155724; }
.status-warning { background: #fff3cd; color: #856404; }
.status-pending { background: #e8eaf6; color: #3949ab; }

.requirements-pill {
  font-size: 0.85rem;
  color: var(--text-secondary, #666);
}

.tab-bar {
  display: flex;
  gap: 0.5rem;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.tab-btn {
  background: none;
  border: none;
  padding: 0.75rem 1rem;
  cursor: pointer;
  color: #666;
  border-bottom: 2px solid transparent;
  font-size: 0.9rem;
}

.tab-btn.active {
  color: #3949ab;
  border-bottom-color: #3949ab;
  font-weight: 600;
}

.tab-btn i {
  margin-right: 0.35rem;
}

.coverage-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.coverage-chip {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  min-width: 5rem;
}

.chip-compliant { border-left: 3px solid #22c55e; }
.chip-partial { border-left: 3px solid #f59e0b; }
.chip-missing { border-left: 3px solid #ef4444; }
.chip-unknown { border-left: 3px solid #94a3b8; }

.chip-type {
  font-weight: 700;
  font-size: 0.8rem;
}

.chip-status {
  font-size: 0.75rem;
  color: #64748b;
  text-transform: capitalize;
}

.chip-score {
  font-size: 0.75rem;
  font-weight: 600;
}

.root-cause-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.root-cause-card {
  border: 1px solid #fecaca;
  border-left: 3px solid #ef4444;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  background: #fff;
}

.cause-title {
  font-weight: 600;
  color: #0f172a;
}

.cause-desc {
  margin: 0.35rem 0 0;
  font-size: 0.875rem;
  color: #475569;
}

.cause-resolution {
  margin: 0.5rem 0 0;
  font-size: 0.85rem;
  color: #4338ca;
}

.cause-resolution i {
  margin-right: 0.35rem;
}

.expiry-list {
  margin: 0;
  padding-left: 1.25rem;
  font-size: 0.9rem;
}

.expiry-days-inline {
  color: #64748b;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #3949ab;
}

.stat-label {
  font-size: 0.8rem;
  color: #666;
  margin-top: 0.25rem;
}

.section-block h2 {
  font-size: 1.1rem;
  margin: 0 0 0.5rem;
}

.section-hint {
  color: #666;
  font-size: 0.85rem;
  margin: 0 0 1rem;
}

.trend-table-wrap,
.parties-table-wrap {
  overflow-x: auto;
}

.trend-table,
.parties-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.trend-table th,
.trend-table td,
.parties-table th,
.parties-table td {
  text-align: left;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #eee;
}

.trend-table th,
.parties-table th {
  font-weight: 600;
  color: #555;
}

.coverage-type-tag {
  display: inline-block;
  font-size: 0.75rem;
  padding: 0.1rem 0.4rem;
  margin: 0.1rem 0.2rem 0.1rem 0;
  border-radius: 4px;
  background: #e0e7ff;
  color: #3730a3;
}

.party-note {
  display: block;
  font-size: 0.8rem;
  color: #64748b;
  font-weight: normal;
}

.contact-cell span {
  display: block;
  font-size: 0.85rem;
}

.muted {
  color: #94a3b8;
}

.locations-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.location-row {
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.section-purpose {
  margin: -0.25rem 0 0.75rem;
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.45;
}

.location-address,
.location-meta,
.location-status {
  display: block;
  font-size: 0.85rem;
  color: #64748b;
}

.location-status {
  color: #9a3412;
}

.timeline-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.timeline-item {
  display: flex;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.timeline-marker {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-top: 0.35rem;
  flex-shrink: 0;
  background: #3949ab;
}

.type-check_run { background: #3949ab; }
.type-document_upload { background: #28a745; }
.type-status_change { background: #ffc107; }
.type-request { background: #17a2b8; }
.type-session { background: #6c757d; }
.type-outreach { background: #7b61ff; }
.timeline-marker.is-problem { background: #dc3545; }

.timeline-title {
  font-weight: 500;
}

.timeline-meta {
  font-size: 0.8rem;
  color: #888;
  margin-top: 0.15rem;
}

.btn-link {
  background: none;
  border: none;
  color: #3949ab;
  cursor: pointer;
  padding: 0;
  font-size: 0.85rem;
  margin-top: 0.25rem;
}

.continuity-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.continuity-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
}

.continuity-card-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.continuity-bar-track {
  height: 8px;
  background: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
}

.continuity-bar-fill {
  height: 100%;
  background: #28a745;
  border-radius: 4px;
}

.continuity-meta {
  font-size: 0.85rem;
  color: #666;
  margin-top: 0.5rem;
}

.lapse-list {
  margin: 0.5rem 0 0;
  padding-left: 1.25rem;
  font-size: 0.85rem;
  color: #c0392b;
}

.interval-list {
  margin-top: 0.75rem;
  border-top: 1px solid #eee;
  padding-top: 0.5rem;
}

.interval-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.82rem;
  padding: 0.35rem 0;
  color: #444;
}

.interval-row.low-confidence {
  color: #9a6700;
}

.interval-badges {
  display: flex;
  gap: 0.35rem;
}

.confidence-badge,
.source-badge {
  font-size: 0.7rem;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  background: #f0f0f0;
  color: #555;
}

.interval-row.low-confidence .confidence-badge {
  background: #fff3cd;
  color: #856404;
}

.empty-hint,
.loading-state,
.error-state {
  text-align: center;
  padding: 2rem;
  color: #666;
}

.load-more-btn {
  margin-top: 1rem;
}

.documents-tab {
  min-height: 500px;
}

.documents-tab :deep(.modal-overlay) {
  position: static;
  background: none;
  display: block;
}

.documents-tab :deep(.docs-modal-container) {
  max-width: 100%;
  max-height: none;
  box-shadow: none;
  border: 1px solid #e9ecef;
}

@media (max-width: 640px) {
  .verdict-card {
    flex-direction: column;
  }

  .verdict-expiry {
    text-align: left;
  }
}
</style>
